/**
 * Resolves which free-shipping threshold the mini cart should show.
 *
 * Extracted from `cart.js` so the A/B arms are unit-testable without mounting the whole cart. The
 * decision is small but it has already been wrong in production twice, in both directions:
 *
 *  1. The original `shipping_threshold` test (TLC-3699) was "free shipping on all orders", so
 *     `variant_b` set the threshold to `0` — which makes `showShippingBar` false and hides the
 *     progress bar entirely. When the design moved to a threshold PAIR ($25 vs $35, SHOPB2C-859,
 *     2026-04-13) the theme was never updated, so a threshold test would have hidden the bar for the
 *     variant arm while checkout still charged shipping below the Function's own variant threshold.
 *     Returning `0` from here is therefore never correct for a threshold test (SHOPB2C-1064).
 *
 *  2. An unset Variant B threshold must fall back to the CONTROL value, not to `0` and not to some
 *     built-in default. A misconfigured test then renders both arms identically — a visible null
 *     result someone will notice — instead of promising free shipping that checkout will not honor,
 *     which is a customer-facing lie and a chargeback conversation.
 *
 * ⚠️ The returned number only controls the CART UI. The money is decided at checkout by the
 * `shoplift-shipping-discount` Shopify Function in `ecom-shopify-lig-extensions-app`, which reads
 * its own thresholds from a discount-node metafield. These two have to be set to the same pair by
 * hand; nothing enforces it across the repo boundary. If they disagree, the cart lies.
 *
 * @param {object} args
 * @param {boolean} args.enabled            `settings.shoplift_test_enabled`
 * @param {string}  args.testKey            `settings.shoplift_test_key`
 * @param {string}  [args.variantKey]       Resolved arm — `variant_a` | `variant_b` | undefined
 * @param {number}  [args.controlThreshold] Cart section `shipping_threshold` (the control arm)
 * @param {number}  [args.variantBThreshold] Cart section `shipping_threshold_variant_b`
 * @returns {number|null|undefined} Threshold for the mini-cart progress bar
 */
export const resolveShippingThreshold = ({
  enabled,
  testKey,
  variantKey,
  controlThreshold,
  variantBThreshold,
}) => {
  const testIsDriving = Boolean(enabled) && testKey === 'shipping_threshold';

  if (testIsDriving && variantKey === 'variant_b') {
    // `||` is deliberate over `??`: 0 and '' are both misconfigurations here, not intent. A Variant B
    // threshold of 0 would hide the bar, which is the exact defect this module exists to prevent.
    return variantBThreshold || controlThreshold;
  }

  // Control, an unresolved test, a different test in the slot, or the test switched off entirely.
  return controlThreshold;
};

export default resolveShippingThreshold;
